import { environment } from '@env/environment';
import { isAppCheckRefused } from '@shared/services/backend/adapters/firebase/callable';
import { kzDetectPlatform } from '@shared/services/platform-capability.service';

/**
 * kzd-ac wave-2 review M4: signup and password reset on the desktop once they are enforced. Both callables consume
 * their App Check token and never take one minted for the desktop app (review M2 a), so a desktop — registered or
 * not — is refused there for want of a token (`appCheck: true`, AC-6). Instead of a dead end, the page says so and
 * offers the env's website (`environment.webUrl`), in the system browser: the website has its own App Check
 * (reCAPTCHA), and the account it makes signs in here as any other. Dormant until enforcement: before it, nothing is
 * refused for want of a token, so nothing here ever shows.
 *
 * The browser: `window.open(url, '_blank', 'noopener')` — the desktop's window handler (electron/customer-display-
 * window.ts `denyAll`) denies the new window and hands an `https:` link to `shell.openExternal`.
 */

/** The seams (for the specs): where the app runs, this env's website, how a link opens. */
export const KZ_DESKTOP_WEBSITE = {
  platform: (): string => kzDetectPlatform(),
  webUrl: (): string | null => (environment as { webUrl?: string | null }).webUrl ?? null,
  open: (url: string): void => void window.open(url, '_blank', 'noopener'),
};

/** The website's page for each door (the app's own routes, which the website serves). */
export type KzWebsitePage = 'signup' | 'forgot-password';

/**
 * The website page to send this person to, or null: only on the desktop, only for an App Check refusal, and only when
 * the env names an `https:` website.
 */
export function desktopWebsiteFallback(error: unknown, page: KzWebsitePage): string | null {
  if (KZ_DESKTOP_WEBSITE.platform() !== 'electron' || !isAppCheckRefused(error)) return null;
  const base = String(KZ_DESKTOP_WEBSITE.webUrl() ?? '').trim().replace(/\/+$/, '');
  return /^https:\/\/[^/\s]+$/.test(base) ? `${base}/${page}` : null;
}
